import { useState } from 'react';
import { Field, Input, Select } from '@zt/ui';
import { HelpTip } from '../../lib/help';
import { useT } from '../../lib/i18n';

/**
 * Rapor dönemi seçimi (F8-01): hazır dönemler önce (bu ay, geçen ay, son 7/30/90 gün, bu yıl, geçen yıl), özel aralık
 * en sonda; gruplama aralığı. Günler Türkiye saatiyle; sunucu aynı kuralla hesaplar.
 */
export const PRESETS = [
  'this_month',
  'last_month',
  'last_7',
  'last_30',
  'last_90',
  'this_year',
  'last_year',
  'custom',
] as const;
export type Preset = (typeof PRESETS)[number];
export type Interval = 'day' | 'week' | 'month' | 'year';

export function istanbulToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul' }).format(new Date()).slice(0, 10);
}

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Hazır dönemin başlangıç ve bitiş günü. */
export function presetRange(
  p: Exclude<Preset, 'custom'>,
  today = istanbulToday(),
): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  switch (p) {
    case 'this_month':
      return { from: `${today.slice(0, 8)}01`, to: today };
    case 'last_month': {
      const first = m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
      return { from: first, to: addDays(`${today.slice(0, 8)}01`, -1) };
    }
    case 'last_7':
      return { from: addDays(today, -6), to: today };
    case 'last_30':
      return { from: addDays(today, -29), to: today };
    case 'last_90':
      return { from: addDays(today, -89), to: today };
    case 'this_year':
      return { from: `${y}-01-01`, to: today };
    case 'last_year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  }
}

/** Dönem uzunluğuna göre varsayılan gruplama (sunucuyla aynı: 62 günden kısa → gün, değilse ay). */
export function defaultInterval(from: string, to: string): Interval {
  const days = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
  return days < 62 ? 'day' : 'month';
}

export function usePeriod(initial: Exclude<Preset, 'custom'> = 'this_month') {
  const [preset, setPreset] = useState<Preset>(initial);
  const [custom, setCustom] = useState(() => presetRange(initial));
  const [intervalChoice, setInterval] = useState<Interval | ''>('');
  const range = preset === 'custom' ? custom : presetRange(preset);
  const interval = intervalChoice || defaultInterval(range.from, range.to);
  const params = new URLSearchParams({ from: range.from, to: range.to, interval });
  return {
    preset,
    setPreset: (p: Preset) => {
      if (p === 'custom' && preset !== 'custom') setCustom(range);
      setPreset(p);
    },
    from: range.from,
    to: range.to,
    setFrom: (from: string) => setCustom((c) => ({ ...c, from })),
    setTo: (to: string) => setCustom((c) => ({ ...c, to })),
    interval,
    intervalChoice,
    setInterval,
    params,
  };
}
export type PeriodState = ReturnType<typeof usePeriod>;

export function PeriodControls({
  period,
  showInterval = true,
}: {
  period: PeriodState;
  /** Gruplama seçimi (seri gösteren raporlarda). Liste ekranlarında gizlenir. */
  showInterval?: boolean;
}) {
  const t = useT();
  return (
    <>
      <Field label={t('admin.reports.period')} help={<HelpTip k="reports.period" />}>
        {(p) => (
          <Select {...p} value={period.preset} onChange={(e) => period.setPreset(e.target.value as Preset)}>
            {PRESETS.map((x) => (
              <option key={x} value={x}>
                {t(`admin.reports.presets.${x}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {period.preset === 'custom' && (
        <>
          <Field label={t('admin.reports.from')}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={period.from}
                max={period.to}
                onChange={(e) => period.setFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.reports.to')}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={period.to}
                min={period.from}
                onChange={(e) => period.setTo(e.target.value)}
              />
            )}
          </Field>
        </>
      )}
      {showInterval && (
        <Field label={t('admin.reports.interval')} help={<HelpTip k="reports.interval" />}>
          {(p) => (
            <Select
              {...p}
              value={period.interval}
              onChange={(e) => period.setInterval(e.target.value as Interval)}
            >
              {(['day', 'week', 'month', 'year'] as const).map((x) => (
                <option key={x} value={x}>
                  {t(`admin.reports.intervals.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </>
  );
}
